import { useState } from "react";
import { Link, useParams } from "react-router-dom";

import { Discussion } from "../components/Discussion";
import { EmptyState, Loading } from "../components/StateViews";
import { apiUrl } from "../lib/api";
import type { RouterOutputs } from "../trpc";
import { trpc } from "../trpc";

type DohaData = NonNullable<RouterOutputs["dohas"]["get"]>;
type NeedsWord = DohaData["needsDefinition"][number];

export function DohaPage() {
  const { slug = "" } = useParams();
  const me = trpc.session.me.useQuery();
  const query = trpc.dohas.get.useQuery({ slug }, { enabled: slug.length > 0 });

  if (query.isLoading) return <Loading label="Opening the doha" />;
  if (!query.data) {
    return (
      <div className="mx-auto max-w-3xl px-5 pt-20 sm:px-8">
        <EmptyState title="No doha at that reference.">
          <Link to="/dohas" className="text-terracotta hover:underline">
            Browse the dohas
          </Link>
        </EmptyState>
      </div>
    );
  }

  const { doha, terms, captures, needsDefinition, myStudy } = query.data;
  const hasText = Boolean(
    doha.hindiText || doha.englishText || doha.commentary,
  );
  const approved = me.data?.approved ?? false;

  return (
    <div className="mx-auto max-w-3xl px-5 pt-14 sm:px-8">
      <Link
        to="/dohas"
        className="eyebrow transition-colors hover:text-terracotta"
      >
        ← Dohas
      </Link>

      <header className="mt-4 flex flex-wrap items-end justify-between gap-4 border-b border-line pb-6">
        <div>
          <p className="font-mono text-sm text-ink-faint">{doha.slug}</p>
          <h1 className="mt-1 font-serif text-3xl text-ink">
            Mandal {doha.mandal} · Chapter {doha.chapter} · Doha {doha.doha}
          </h1>
        </div>
        {approved ? (
          <StudyToggle dohaSlug={doha.slug} inStudy={myStudy.inStudy} />
        ) : null}
      </header>

      {hasText ? (
        <article className="mt-8 space-y-6">
          {doha.hindiText ? (
            <p className="font-deva text-2xl leading-relaxed text-ink">
              {doha.hindiText}
            </p>
          ) : null}
          {doha.englishText ? (
            <p className="font-serif text-xl italic leading-relaxed text-ink-soft">
              {doha.englishText}
            </p>
          ) : null}
          {doha.commentary ? (
            <p className="leading-relaxed text-ink-soft">{doha.commentary}</p>
          ) : null}
        </article>
      ) : null}

      {/* Shared photos — the community's documentation of this doha. */}
      {approved ? (
        <section className="mt-8">
          <div className="flex items-center justify-between">
            <h2 className="eyebrow">
              Photos of this doha
              {captures.length > 0 ? ` (${captures.length})` : ""}
            </h2>
            <Link
              to={`/capture?doha=${doha.slug}`}
              className="text-sm font-medium text-terracotta hover:underline"
            >
              {captures.length > 0 ? "Add a photo" : "Capture it"}
            </Link>
          </div>
          {captures.length > 0 ? (
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3">
              {captures.map((c) => (
                <Link
                  key={c.id}
                  to={`/capture/${c.id}`}
                  className="group relative block overflow-hidden rounded-xl border border-line bg-paper-raised transition-all hover:-translate-y-0.5 hover:border-terracotta/50"
                >
                  <div className="aspect-[4/3] overflow-hidden bg-paper-sunk">
                    <img
                      src={apiUrl(c.mediaPath)}
                      alt="A capture of this doha"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                  {c.isMine ? (
                    <span className="absolute right-1.5 top-1.5 rounded-full bg-ink/80 px-2 py-0.5 text-[0.65rem] font-medium text-paper">
                      Yours
                    </span>
                  ) : null}
                </Link>
              ))}
            </div>
          ) : (
            <p className="mt-3 text-sm text-ink-faint">
              No one has photographed this doha yet — be the first.
            </p>
          )}
        </section>
      ) : null}

      {!hasText && !approved ? (
        <div className="mt-8 rounded-2xl border border-dashed border-line-strong bg-paper-raised/60 px-6 py-8 text-center">
          <p className="font-serif text-lg text-ink">
            The verse itself isn’t digitised yet.
          </p>
          <p className="mt-1 text-sm text-ink-faint">
            Its words are known below.
          </p>
        </div>
      ) : null}

      <section className="mt-10">
        <h2 className="eyebrow">Words in this doha ({terms.length})</h2>
        {terms.length > 0 ? (
          <div className="mt-4 flex flex-wrap gap-2">
            {terms.map((term) => (
              <Link
                key={term.slug}
                to={`/glossary/${term.slug}`}
                className="rounded-full border border-line bg-paper-raised px-3.5 py-1.5 font-serif text-base text-ink transition-colors hover:border-terracotta/50 hover:text-terracotta"
              >
                {term.displayName}
              </Link>
            ))}
          </div>
        ) : (
          <p className="mt-3 text-sm text-ink-faint">
            No glossary words recorded for this doha.
          </p>
        )}
      </section>

      {/* Shared: words the community flagged as needing a translation. */}
      {approved && needsDefinition.length > 0 ? (
        <section className="mt-10">
          <h2 className="eyebrow">
            Needs a definition · shared with translators
          </h2>
          <p className="mt-1 text-sm text-ink-faint">
            Words readers have marked on the photos. Translators answer here for
            everyone.
          </p>
          <div className="mt-4 space-y-3">
            {needsDefinition.map((w) => (
              <NeedsDefinitionCard key={w.sampleAnnotationId} word={w} />
            ))}
          </div>
        </section>
      ) : null}

      {/* Private: your own notes, seen by no one else. */}
      {approved ? (
        <DohaNotes dohaSlug={doha.slug} initialNote={myStudy.note} />
      ) : null}
    </div>
  );
}

function StudyToggle({
  dohaSlug,
  inStudy,
}: {
  dohaSlug: string;
  inStudy: boolean;
}) {
  const utils = trpc.useUtils();
  const invalidate = () => utils.dohas.get.invalidate({ slug: dohaSlug });
  const add = trpc.study.add.useMutation({ onSuccess: invalidate });
  const remove = trpc.study.remove.useMutation({ onSuccess: invalidate });
  const busy = add.isPending || remove.isPending;

  return inStudy ? (
    <button
      type="button"
      disabled={busy}
      onClick={() => remove.mutate({ dohaSlug })}
      className="rounded-full border border-terracotta/50 bg-terracotta-wash px-4 py-2 text-sm font-medium text-terracotta transition-colors hover:bg-terracotta-wash/70 disabled:opacity-50"
    >
      ✓ In your study
    </button>
  ) : (
    <button
      type="button"
      disabled={busy}
      onClick={() => add.mutate({ dohaSlug })}
      className="rounded-full border border-line-strong px-4 py-2 text-sm font-medium text-ink transition-colors hover:bg-paper-sunk disabled:opacity-50"
    >
      Add to my study
    </button>
  );
}

function NeedsDefinitionCard({ word }: { word: NeedsWord }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-line bg-paper-raised p-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <span className="font-serif text-xl text-ink">{word.label}</span>
          {word.count > 1 ? (
            <span className="ml-2 text-sm text-ink-faint">
              marked by {word.count}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-sm font-medium text-terracotta hover:underline"
        >
          {open ? "Hide" : "Discuss / answer"}
        </button>
      </div>
      {open ? (
        <div className="mt-4 border-t border-line pt-4">
          <Discussion
            subjectType="annotation"
            subjectId={word.sampleAnnotationId}
          />
        </div>
      ) : null}
    </div>
  );
}

function DohaNotes({
  dohaSlug,
  initialNote,
}: {
  dohaSlug: string;
  initialNote: string | null;
}) {
  const utils = trpc.useUtils();
  const [note, setNote] = useState(initialNote ?? "");
  const [dirty, setDirty] = useState(false);
  const save = trpc.study.setNote.useMutation({
    onSuccess: () => {
      setDirty(false);
      void utils.dohas.get.invalidate({ slug: dohaSlug });
    },
  });

  return (
    <section className="mt-10 rounded-2xl border border-line bg-paper-sunk/40 p-5">
      <div className="flex items-center gap-2">
        <h2 className="eyebrow">Your notes</h2>
        <span className="rounded-full bg-paper px-2 py-0.5 text-[0.65rem] font-semibold uppercase tracking-wide text-ink-faint">
          Private
        </span>
      </div>
      <p className="mt-1 text-sm text-ink-faint">
        Only you can see this. Kept in your study.
      </p>
      <textarea
        value={note}
        onChange={(e) => {
          setNote(e.target.value);
          setDirty(true);
        }}
        rows={4}
        placeholder="What you’re noticing in this doha…"
        className="mt-3 w-full rounded-xl border border-line-strong bg-paper-raised px-4 py-3 font-serif text-lg leading-relaxed text-ink outline-none focus:border-terracotta/60"
      />
      <div className="mt-2 flex items-center justify-end gap-3">
        <span className="text-sm text-ink-faint">
          {save.isPending ? "Saving…" : !dirty && save.isSuccess ? "Saved" : ""}
        </span>
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate({ dohaSlug, note })}
          className="rounded-full bg-ink px-5 py-2 text-sm font-medium text-paper transition-opacity hover:opacity-90 disabled:opacity-40"
        >
          Save note
        </button>
      </div>
    </section>
  );
}
